import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, ClipboardCheck, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { FreeToggle } from './FreeToggle';
import { QuizForm, emptyQuiz } from './quizzes/QuizForm';

const QuizzesManager = () => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => api.get('/admin/quizzes').then(({ data }) => setItems(data || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/quizzes/${form.id}`, form); else await api.post('/quizzes', form);
      toast.success(L('تم حفظ الاختبار', 'Quiz saved'));
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || L('تعذر الحفظ', 'Could not save')); }
  };
  const remove = async (q) => {
    if (!window.confirm(L('حذف هذا الاختبار؟', 'Delete this quiz?'))) return;
    await api.delete(`/quizzes/${q.id}`);
    setItems(xs => xs.filter(x => x.id !== q.id));
    toast.success(L('تم الحذف', 'Deleted'));
  };
  const toggleFree = async (q) => {
    const { data } = await api.patch(`/quizzes/${q.id}/free`);
    setItems(xs => xs.map(x => (x.id === q.id ? { ...x, is_free: data.is_free } : x)));
  };

  return (
    <div data-testid="quizzes-manager" className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="font-extrabold text-slate-900 text-lg">{L('إدارة الاختبارات', 'Manage quizzes')}</h3>
          <p className="text-slate-500 text-sm">{items.length} {L('اختبار', 'quizzes')}</p>
        </div>
        <button data-testid="quiz-add-btn" onClick={() => setEditing(emptyQuiz())} className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition-colors"><Plus className="w-4 h-4" />{L('اختبار جديد', 'New quiz')}</button>
      </div>
      {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        : items.length === 0 ? <div className="text-center text-slate-500 py-10">{L('لا توجد اختبارات بعد', 'No quizzes yet')}</div>
        : (
          <ul className="divide-y divide-slate-100">
            {items.map(q => (
              <li key={q.id} data-testid={`admin-quiz-row-${q.id}`} className="flex flex-wrap items-center gap-3 py-3">
                <ClipboardCheck className="w-5 h-5 text-emerald-500 shrink-0" />
                <div className="flex-1 min-w-[180px]">
                  <div className="font-bold text-slate-900 text-sm flex items-center gap-2">{q.titleAr}{!q.published && <EyeOff className="w-3.5 h-3.5 text-slate-400" />}</div>
                  <div className="text-xs text-slate-400">{(q.questions || []).length} {L('سؤال', 'questions')} • {q.attempts || 0} {L('محاولة', 'attempts')}</div>
                </div>
                <FreeToggle value={q.is_free} onChange={() => toggleFree(q)} testId={`admin-quiz-free-${q.id}`} />
                <button data-testid={`admin-quiz-edit-${q.id}`} onClick={() => setEditing(q)} className="p-2 rounded-lg text-blue-600 hover:bg-blue-50"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`admin-quiz-delete-${q.id}`} onClick={() => remove(q)} className="p-2 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl bg-white">
          <DialogHeader>
            <DialogTitle>{editing?.id ? L('تعديل الاختبار', 'Edit quiz') : L('اختبار جديد', 'New quiz')}</DialogTitle>
            <DialogDescription>{L('حدّد الإجابة الصحيحة لكل سؤال بالدائرة بجانب الخيار', 'Mark the correct answer with the circle next to each option')}</DialogDescription>
          </DialogHeader>
          {editing && <QuizForm key={editing.id || 'new'} initial={editing} onSave={save} onCancel={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuizzesManager;
